import React, { useState } from 'react';
import { Form, Input, Upload, message } from 'antd';
import axios from 'axios';
import { API_BASE } from '../../utils/api';
import { Hero } from '../Hero/Hero';


const MAX_SIZE = 25 * 1024 * 1024;
const SUBMISSION_TYPES = ['.pdf', '.csv', '.xlsx', '.xls'];
const DATASET_TYPES = ['.csv', '.xlsx', '.xls'];

const validateFile = (file: File, allowed: string[]): boolean => {
	const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
	if (!allowed.includes(ext)) {
		message.error(`Unsupported format: ${ext}. Allowed: ${allowed.join(', ')}`);
		return false;
	}
	if (file.size > MAX_SIZE) {
		message.error('File exceeds 25 MB limit');
		return false;
	}
	return true;
};

const Contribute = () => {
	const [form] = Form.useForm();
	const [submissionFile, setSubmissionFile] = useState<File>();
	const [datasetFile, setDatasetFile] = useState<File>();
	const [sending, setSending] = useState(false);

	const onFinish = async (values: any) => {
		const fd = new FormData();
		['name', 'organization', 'email', 'comments'].forEach((k) => fd.append(k, values[k] ?? ''));
		if (submissionFile) fd.append('submissionForm', submissionFile);
		if (datasetFile) fd.append('dataset', datasetFile);
		setSending(true);
		try {
			await axios.post(`${API_BASE}/contribute`, fd);
			message.success('Submission sent. Thank you!');
			form.resetFields();
			setSubmissionFile(undefined);
			setDatasetFile(undefined);
		} catch {
			message.error('Submission failed. Please try again.');
		} finally {
			setSending(false);
		}
	};

	return (
		<div className="contribute-data-page" style={{ padding: 0, margin: 0, maxWidth: 'none' }}>
			<Hero slim title="Contribute Your Dataset" eyebrow="Contribute" />

			<div className="contribute-body">
				{/* Introduction */}
				<section className="contribute-intro">
					<p>
						We invite geoscientists and researchers worldwide to contribute their geofluid datasets to the Geofluid DataHub. By sharing your data, you can help expand a valuable resource that benefits a global community of researchers working on topics like geothermal energy, mineral extraction, and environmental studies.
					</p>
				</section>

				{/* How to Contribute + Why Contribute — wrapped in panel */}
				<div className="contribute-tips-panel">
					{/* Why Contribute */}
					<section className="why-contribute">
						<h2>Why Contribute?</h2>
						<p>
							Your data can help drive innovation and discovery. Geofluid research is essential in addressing some of the world&apos;s most pressing challenges, including the transition to renewable energy, sustainable mining practices, and environmental protection. By contributing, you also foster collaboration and exchange of knowledge within the scientific community.
						</p>
					</section>

					{/* How to Contribute */}
					<section className="how-to-contribute">
						<h2>How to Contribute</h2>
						<ol>
							<li>Download the GSF Data <strong>Data Submission Form</strong> and <strong>QAQC Checklist</strong> and fill in the required details.</li>
							<li>Prepare your dataset in one of the accepted formats (CSV, Excel).</li>
							<li>Upload your completed submission form and dataset using the form below.</li>
							<li>Agree to the terms and conditions for data submission, ensuring that all information stated in the submission form is accurate to the best of your knowledge.</li>
						</ol>
						<div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
							<button
								type="button"
								className="btn-ghost"
								onClick={() => {
									message.info('The GSF Data Submission Form is currently being prepared. Please contact us to receive it.');
									document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
								}}
							>
								Download GSF Data Submission Form
							</button>
							<button
								type="button"
								className="btn-ghost"
								onClick={() => {
									message.info('The QAQC Checklist is currently being prepared. Please contact us to receive it.');
									document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
								}}
							>
								Download QAQC Checklist
							</button>
						</div>
					</section>

					{/* Terms of Contribution */}
					<section className="terms-contribution">
						<h2>Terms of Contribution</h2>
						<p>
							By contributing data, you agree that your dataset will be reviewed and made accessible to the global scientific community for research and educational purposes while cited appropriately.
						</p>
					</section>
				</div>

				{/* Contribution Form */}
				<section className="contribution-form">
					<h2>Submission Form</h2>
					<Form form={form} layout="vertical" onFinish={onFinish}>
						{/* Full Name Field */}
						<Form.Item
							label="Full Name"
							name="name"
							rules={[{ required: true, message: 'Please enter your full name' }]}
						>
							<Input placeholder="Your Full Name" />
						</Form.Item>

						{/* Organization/Institution Field */}
						<Form.Item
							label="Organization/Institution"
							name="organization"
							rules={[{ required: true, message: 'Please enter your organization' }]}
						>
							<Input placeholder="Your Organization/Institution" />
						</Form.Item>

						{/* Email Field */}
						<Form.Item
							label="Email Address"
							name="email"
							rules={[{ required: true, type: 'email', message: 'Please enter a valid email address' }]}
						>
							<Input placeholder="Your Email Address" />
						</Form.Item>

						{/* Comments Field */}
						<Form.Item label="Comments (optional)" name="comments">
							<Input.TextArea placeholder="Any additional information (optional)" rows={4} />
						</Form.Item>

						{/* Upload Submission Form */}
						<Form.Item
							label="Upload Submission Form"
							name="submissionForm"
						>
							<Upload
								name="submissionForm"
								accept={SUBMISSION_TYPES.join(',')}
								beforeUpload={(file) => { if (!validateFile(file, SUBMISSION_TYPES)) return Upload.LIST_IGNORE; setSubmissionFile(file); return false; }}
								maxCount={1}
							>
								<button type="button" className={'btn-ghost'}>Upload Submission Form (PDF/CSV/Excel)</button>
							</Upload>
						</Form.Item>

						{/* Upload Dataset */}
						<Form.Item
							label="Upload Dataset"
							name="dataset"
						>
							<Upload
								name="dataset"
								accept={DATASET_TYPES.join(',')}
								beforeUpload={(file) => { if (!validateFile(file, DATASET_TYPES)) return Upload.LIST_IGNORE; setDatasetFile(file); return false; }}
								maxCount={1}
							>
								<button type="button" className={'btn-ghost'}>Upload Dataset (CSV/Excel)</button>
							</Upload>
						</Form.Item>

						{/* Submit Button */}
						<Form.Item>
							<button
								type="submit"
								className={'cta-btn'}
								style={{ marginTop: '20px' }}
								disabled={sending}
							>
								Submit Data
							</button>
						</Form.Item>
					</Form>
				</section>

				{/* Contact Information */}
				<section id="contact" className="contact-details contribute-contact-panel">
					<h2>Contact Information</h2>
					<p>
						For any questions about data contribution or the submission process, please reach out to us:
					</p>
					<p>
						<strong>Konstantin Nazarov</strong><br />
						Department of Earth Sciences, University of Oxford<br />
						Email: <a href="mailto:konstantin.nazarov@stx.ox.ac.uk">konstantin.nazarov@stx.ox.ac.uk</a><br />
						Phone: 07539475597
					</p>
				</section>
			</div>
		</div>
	);
};

export default Contribute;
